import {
  BarChart3,
  Bell,
  Building2,
  CalendarClock,
  Car,
  CheckCircle2,
  Download,
  ExternalLink,
  Heart,
  LineChart,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Timeline,
  UserCheck,
  Users,
} from 'lucide-react-native';
import type { ComponentType } from 'react';
import {
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';

import { BrandLogo } from '@/src/components/BrandLogo';
import { Button } from '@/src/components/Button';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type LucideIcon = ComponentType<{
  color?: string;
  size?: number;
  strokeWidth?: number;
}>;

type DeckSection = {
  body: string;
  eyebrow: string;
  Icon: LucideIcon;
  title: string;
};

type ProductCapture = {
  body: string;
  eyebrow: string;
  title: string;
  variant: 'feed' | 'comments' | 'garage' | 'trust';
};

type MarketStat = {
  company: string;
  context: string;
  metric: string;
  sourceLabel: string;
  sourceUrl: string;
  value: string;
};

type Differentiator = {
  body: string;
  label: string;
  title: string;
};

type Faq = {
  answer: string;
  question: string;
};

type RepublicExplainer = {
  body: string;
  title: string;
};

const deckSections: DeckSection[] = [
  {
    body: 'Frienzzi is being built as a more positive social network for real friends, centered on posts, comments, expressive reactions, invites, and transparent product updates.',
    eyebrow: '01 Mission',
    Icon: Sparkles,
    title: 'A healthier place for everyday connection.',
  },
  {
    body: 'Large social feeds often reward outrage, anonymous noise, and engagement loops that make people feel worse after checking in.',
    eyebrow: '02 Problem',
    Icon: Bell,
    title: 'Social media has a trust and attention problem.',
  },
  {
    body: 'Frienzzi focuses the experience around people you choose, clear privacy controls, friend-first discovery, and conversations tied to the original moment.',
    eyebrow: '03 Solution',
    Icon: Users,
    title: 'A friend-first network with calmer social mechanics.',
  },
  {
    body: 'The current product includes web/PWA access, posts with media, threaded comments, profile identity, Garage pages, notifications, and public transparency updates.',
    eyebrow: '04 Product',
    Icon: MessageCircle,
    title: 'A working product, not just an idea.',
  },
  {
    body: 'Email verification, real-name expectations, age gating, account deletion flows, granular notifications, and careful privacy wording are part of the product foundation.',
    eyebrow: '05 Trust',
    Icon: ShieldCheck,
    title: 'Trust and safety are designed into the account flow.',
  },
  {
    body: 'Frienzzi publishes plain-language changelog entries for meaningful functionality, privacy, safety, notification, and feed behavior changes.',
    eyebrow: '06 Transparency',
    Icon: Timeline,
    title: 'Users can see how the platform changes over time.',
  },
  {
    body: 'Garage gives automotive communities a focused wedge: vehicle profiles, build stories, public links, QR meet placards, and positive conversation around real interests.',
    eyebrow: '07 Community',
    Icon: Car,
    title: 'Interest communities can grow inside the social graph.',
  },
  {
    body: 'The qualitative business opportunity starts with trusted communities and brand partnerships that respect the user experience instead of overwhelming it.',
    eyebrow: '08 Opportunity',
    Icon: Building2,
    title: 'Brand partnerships can fit naturally around communities.',
  },
  {
    body: 'The roadmap stays focused on product depth: stronger onboarding, richer communities, safer notifications, better sharing, and reliability that can support growth.',
    eyebrow: '09 Roadmap',
    Icon: CalendarClock,
    title: 'The next phase is disciplined product growth.',
  },
  {
    body: 'Frienzzi is preparing Republic materials separately. This page is company-story context only, with no securities offering terms or investment commitments.',
    eyebrow: '10 Republic',
    Icon: UserCheck,
    title: 'Republic campaign coming soon.',
  },
];

const marketStats: MarketStat[] = [
  {
    company: 'Meta',
    context: 'Family of Apps daily active people and full-year revenue',
    metric: '3.58B DAP | $200.97B FY 2025 revenue',
    sourceLabel: 'Meta Q4/FY 2025 results',
    sourceUrl:
      'https://investor.atmeta.com/investor-news/press-release-details/2026/Meta-Reports-Fourth-Quarter-and-Full-Year-2025-Results/',
    value: '3.58B',
  },
  {
    company: 'Snap',
    context: 'Global Snapchat monthly active users in Q4 2025',
    metric: '946M MAU',
    sourceLabel: 'Snap Q4/FY 2025 results',
    sourceUrl:
      'https://investor.snap.com/news/news-details/2026/Snap-Inc--Announces-Fourth-Quarter-and-Full-Year-2025-Financial-Results/default.aspx',
    value: '946M',
  },
  {
    company: 'Reddit',
    context: 'Daily active uniques and Q2 2025 revenue',
    metric: '110.4M DAUq | $500M Q2 2025 revenue',
    sourceLabel: 'Reddit Q2 2025 results',
    sourceUrl:
      'https://investor.redditinc.com/news-events/news-releases/news-details/2025/Reddit-Announces-Second-Quarter-2025-Results/default.aspx',
    value: '110.4M',
  },
  {
    company: 'Pinterest',
    context: 'Global monthly active users and Q2 2025 revenue',
    metric: '578M MAU | $998M Q2 2025 revenue',
    sourceLabel: 'Pinterest Q2 2025 results',
    sourceUrl:
      'https://investor.pinterestinc.com/news-and-events/press-releases/press-releases-details/2025/Pinterest-Announces-Second-Quarter-2025-Results-Delivers-17-Revenue-Growth-and-Record-Users/default.aspx',
    value: '578M',
  },
];

const captures: ProductCapture[] = [
  {
    body: 'A home feed built around real identities, post visibility, media, and reactions beyond a single like.',
    eyebrow: 'Home feed',
    title: 'Fresh posts from real people',
    variant: 'feed',
  },
  {
    body: 'Threaded comments keep replies close to the original post and avoid reshuffling while someone is actively reading.',
    eyebrow: 'Conversation',
    title: 'Comments that stay connected',
    variant: 'comments',
  },
  {
    body: 'Garage profiles turn car communities into a focused growth wedge with public vehicle pages and QR sharing.',
    eyebrow: 'Garage',
    title: 'A home for builds and car friends',
    variant: 'garage',
  },
  {
    body: 'Public changelog entries make meaningful platform behavior changes easier for users to understand.',
    eyebrow: 'Transparency',
    title: 'Trust through public updates',
    variant: 'trust',
  },
];

const whyNowPoints = [
  'Social fatigue is real, but people still want places to share with people they know.',
  'Trust, identity, and control are becoming more important than endless anonymous reach.',
  'Focused communities can create stronger early growth loops than broad feeds from day one.',
  'Community-powered funding fits Frienzzi because supporters can follow the company story before Republic details exist.',
] as const;

const differentiators: Differentiator[] = [
  {
    body: 'Massive reach, but users often have to accept noisy feeds, opaque ranking, and engagement mechanics they do not understand.',
    label: 'Big social networks',
    title: 'Scale without enough trust',
  },
  {
    body: 'High trust between known people, but conversations are fragmented, hard to discover, and difficult to turn into durable communities.',
    label: 'Private groups and chats',
    title: 'Trust without enough structure',
  },
  {
    body: 'Public posts, friends-only sharing, interest communities, real profiles, and public product transparency live in one product.',
    label: 'Frienzzi',
    title: 'Connection with visible accountability',
  },
];

const founderPoints = [
  'Build the social product around people instead of anonymous engagement.',
  'Keep policy, privacy, and product behavior understandable to normal users.',
  'Grow through trusted communities before broad, noisy scale.',
] as const;

const momentumPoints = [
  'Product polish around posting, commenting, media, onboarding, and PWA reliability.',
  'Garage and other focused community wedges that make early sharing easier to understand.',
  'Trust and safety systems that keep account quality, privacy, and notifications manageable.',
  'Founder-led community growth that turns supporters into early product advocates.',
] as const;

const republicExplainers: RepublicExplainer[] = [
  {
    body: 'Republic is a private-market investment platform that hosts official campaign pages, disclosures, and investor workflows for eligible offerings.',
    title: 'A platform for official campaign materials',
  },
  {
    body: 'For supporters, the point is access: if a Frienzzi campaign goes live, Republic is where eligible people would review the official materials and decide whether to participate.',
    title: 'A path for regular supporters',
  },
  {
    body: 'Republic explains that accepted companies prepare their offering page and Form C disclosures, and the offering page cannot go live until the Form C has been filed with the SEC.',
    title: 'Disclosure comes before launch',
  },
  {
    body: 'Frienzzi is not accepting investments on this website. Any future terms, risks, disclosures, and commitments would live through Republic if a campaign opens.',
    title: 'Frienzzi.com stays company context',
  },
];

const faqs: Faq[] = [
  {
    answer:
      'No. This page is company context only. If Frienzzi opens a Republic campaign, offering materials and any investment process will happen through Republic.',
    question: 'Is this an investment offer?',
  },
  {
    answer:
      'Republic can give regular supporters a way to participate if they are eligible and if a campaign goes live.',
    question: 'Why Republic?',
  },
  {
    answer:
      'Yes. Frienzzi is a working product and is being actively built, tested, and improved.',
    question: 'Is Frienzzi live?',
  },
  {
    answer:
      'Focused communities make the early product easier to understand, share, and grow before expanding into broader social use cases.',
    question: 'Why start with Garage and communities?',
  },
];

const investorPagePublishedLabel = 'Published May 15, 2026';
const investorPageUpdatedLabel = 'Updated May 15, 2026';

function openPdf() {
  if (Platform.OS === 'web') {
    window.location.assign('/investors/frienzzi-investor-deck.pdf');
  }
}

function openSource(url: string) {
  void Linking.openURL(url);
}

function MetricFreePill({ children }: { children: string }) {
  return (
    <View style={styles.pill} testID="investor-pdf-card">
      <Text style={styles.pillText}>{children}</Text>
    </View>
  );
}

function DeckSectionCard({ body, eyebrow, Icon, title }: DeckSection) {
  return (
    <View style={styles.sectionCard} testID="investor-pdf-card">
      <View style={styles.sectionIcon}>
        <Icon color={colors.primary} size={24} strokeWidth={2.5} />
      </View>
      <Text style={styles.eyebrow}>{eyebrow}</Text>
      <Text style={styles.sectionCardTitle}>{title}</Text>
      <Text style={styles.sectionCardBody}>{body}</Text>
    </View>
  );
}

function FeedCapture() {
  return (
    <View style={styles.captureScreen}>
      <View style={styles.captureTopBar}>
        <BrandLogo variant="icon" size="sm" />
        <Text style={styles.captureTopText}>Home</Text>
        <View style={styles.captureDot} />
      </View>
      <View style={styles.demoPost}>
        <View style={styles.demoIdentityRow}>
          <View style={styles.demoAvatar} />
          <View>
            <Text style={styles.demoName}>Maya Johnson</Text>
            <Text style={styles.demoMeta}>Friends post</Text>
          </View>
        </View>
        <Text style={styles.demoBody}>
          Weekend cruise photos are up. The sunset made every color pop.
        </Text>
        <View style={styles.demoMedia}>
          <Sparkles color={colors.surface} size={28} strokeWidth={2.6} />
        </View>
        <View style={styles.demoActionRow}>
          <Heart color={colors.primary} size={18} strokeWidth={2.5} />
          <Text style={styles.demoActionText}>love</Text>
          <MessageCircle color={colors.secondary} size={18} strokeWidth={2.5} />
          <Text style={styles.demoActionText}>12 comments</Text>
        </View>
      </View>
    </View>
  );
}

function CommentsCapture() {
  return (
    <View style={styles.captureScreen}>
      <View style={styles.captureHeaderRow}>
        <Text style={styles.captureTitle}>Comments</Text>
        <Text style={styles.captureSort}>Most recent</Text>
      </View>
      {[
        'Love this update.',
        'The new layout feels calmer.',
        'Can we add the garage link?',
      ].map((comment, index) => (
        <View key={comment} style={styles.commentRow}>
          <View
            style={[
              styles.commentAvatar,
              index === 1 ? styles.commentAvatarAlt : null,
            ]}
          />
          <View style={styles.commentBubble}>
            <Text style={styles.commentName}>
              {index === 0 ? 'Ari' : index === 1 ? 'Jason' : 'Taylor'}
            </Text>
            <Text style={styles.commentText}>{comment}</Text>
          </View>
        </View>
      ))}
      <View style={styles.composerPreview}>
        <Text style={styles.composerText}>Write a comment...</Text>
        <View style={styles.composerSend}>
          <Text style={styles.composerSendText}>Post</Text>
        </View>
      </View>
    </View>
  );
}

function GarageCapture() {
  return (
    <View style={styles.captureScreen}>
      <View style={styles.garageHero}>
        <Car color={colors.surface} size={34} strokeWidth={2.6} />
      </View>
      <Text style={styles.garageTitle}>1997 Toyota Supra</Text>
      <Text style={styles.garageMeta}>Single turbo build | Weekend car</Text>
      <View style={styles.garageStats}>
        <View style={styles.garageStat}>
          <Text style={styles.garageStatValue}>Photos</Text>
          <Text style={styles.garageStatLabel}>Build story</Text>
        </View>
        <View style={styles.garageStat}>
          <Text style={styles.garageStatValue}>QR</Text>
          <Text style={styles.garageStatLabel}>Meet card</Text>
        </View>
      </View>
    </View>
  );
}

function TrustCapture() {
  return (
    <View style={styles.captureScreen}>
      <View style={styles.trustHeader}>
        <Timeline color={colors.primary} size={24} strokeWidth={2.6} />
        <View>
          <Text style={styles.trustTitle}>Public Changelog</Text>
          <Text style={styles.trustMeta}>Plain-language updates</Text>
        </View>
      </View>
      {[
        'Email verification for new accounts',
        'Smoother comment writing',
        'Privacy and terms clarity',
      ].map((item) => (
        <View key={item} style={styles.trustRow}>
          <CheckCircle2 color={colors.success} size={18} strokeWidth={2.6} />
          <Text style={styles.trustText}>{item}</Text>
        </View>
      ))}
    </View>
  );
}

function ProductCaptureCard({ body, eyebrow, title, variant }: ProductCapture) {
  let Capture = FeedCapture;

  if (variant === 'comments') {
    Capture = CommentsCapture;
  } else if (variant === 'garage') {
    Capture = GarageCapture;
  } else if (variant === 'trust') {
    Capture = TrustCapture;
  }

  return (
    <View style={styles.captureCard} testID="investor-pdf-card">
      <Capture />
      <View style={styles.captureCopy}>
        <Text style={styles.eyebrow}>{eyebrow}</Text>
        <Text style={styles.captureCardTitle}>{title}</Text>
        <Text style={styles.captureCardBody}>{body}</Text>
      </View>
    </View>
  );
}

function MarketStatCard({
  company,
  context,
  metric,
  sourceLabel,
  sourceUrl,
  value,
}: MarketStat) {
  return (
    <View style={styles.marketStatCard} testID="investor-pdf-card">
      <Text style={styles.marketCompany}>{company}</Text>
      <Text style={styles.marketValue}>{value}</Text>
      <Text style={styles.marketMetric}>{metric}</Text>
      <Text style={styles.marketContext}>{context}</Text>
      <Pressable
        accessibilityRole="link"
        onPress={() => openSource(sourceUrl)}
        style={({ pressed }) => [
          styles.marketSourceLink,
          pressed ? styles.marketSourceLinkPressed : null,
        ]}
      >
        <Text style={styles.marketSourceText}>{sourceLabel}</Text>
        <ExternalLink color={colors.secondary} size={15} strokeWidth={2.6} />
      </Pressable>
    </View>
  );
}

function DifferentiatorCard({ body, label, title }: Differentiator) {
  return (
    <View style={styles.differentiatorCard} testID="investor-pdf-card">
      <Text style={styles.differentiatorLabel}>{label}</Text>
      <Text style={styles.differentiatorTitle}>{title}</Text>
      <Text style={styles.differentiatorBody}>{body}</Text>
    </View>
  );
}

function FaqItem({ answer, question }: Faq) {
  return (
    <View style={styles.faqItem} testID="investor-pdf-card">
      <Text style={styles.faqQuestion}>{question}</Text>
      <Text style={styles.faqAnswer}>{answer}</Text>
    </View>
  );
}

function RepublicExplainerCard({ body, title }: RepublicExplainer) {
  return (
    <View style={styles.republicExplainerCard} testID="investor-pdf-card">
      <Text style={styles.republicExplainerTitle}>{title}</Text>
      <Text style={styles.republicExplainerBody}>{body}</Text>
    </View>
  );
}

export default function InvestorsPage() {
  const { width } = useWindowDimensions();
  const compact = width < 760;

  return (
    <PublicPageShell maxWidth={1160} showFooterAddress={false}>
      <View
        style={[styles.hero, compact ? styles.heroCompact : null]}
        testID="investor-pdf-hero"
      >
        <View style={[styles.heroCopy, compact ? styles.panelCompact : null]}>
          <View style={styles.brandRow}>
            <BrandLogo variant="icon" size="md" />
            <Text style={styles.brandText} numberOfLines={1}>
              frienzzi investors
            </Text>
          </View>
          <Text
            style={[styles.heroTitle, compact ? styles.heroTitleCompact : null]}
          >
            Community crowdfunding can let users do more than watch Frienzzi
            grow.
          </Text>
          <Text style={styles.heroSubtitle}>
            Frienzzi is preparing a Republic path so supporters can follow the
            company story now and, if a campaign goes live, review the official
            materials before deciding whether to participate.
          </Text>
          <View style={styles.communityFundingCallout}>
            <View style={styles.communityFundingIcon}>
              <Users color={colors.surface} size={24} strokeWidth={2.7} />
            </View>
            <View style={styles.communityFundingCopy}>
              <Text style={styles.communityFundingEyebrow}>
                Community-powered funding
              </Text>
              <Text style={styles.communityFundingTitle}>
                We want the people who believe in Frienzzi to have a chance to
                own a piece of the company.
              </Text>
              <Text style={styles.communityFundingBody}>
                If a Republic campaign opens, investment details and commitments
                will happen on Republic, not on this page.
              </Text>
            </View>
          </View>
          <View
            style={[
              styles.heroActions,
              compact ? styles.heroActionsCompact : null,
            ]}
            testID="investor-web-actions"
          >
            <Button
              disabled
              title="Republic campaign coming soon"
              style={compact ? styles.actionFull : styles.actionButton}
            />
            <Button
              onPress={openPdf}
              title="Download PDF deck"
              variant="secondary"
              style={compact ? styles.actionFull : styles.actionButton}
            />
          </View>
          <Text
            style={[
              styles.disclaimer,
              compact ? styles.disclaimerCompact : null,
            ]}
          >
            This page is company-story context only. It does not include
            offering terms, request money, or accept investment commitments.
          </Text>
        </View>
        <View
          style={[
            styles.heroPreview,
            compact ? [styles.panelCompact, styles.heroPreviewCompact] : null,
          ]}
        >
          <View
            style={[
              styles.previewTop,
              compact ? styles.previewTopCompact : null,
            ]}
          >
            <BarChart3
              color={colors.surface}
              size={compact ? 22 : 30}
              strokeWidth={2.6}
            />
            <View style={styles.previewTitleWrap}>
              <Text style={styles.previewEyebrow}>Investor story</Text>
              <Text
                style={[
                  styles.previewTitle,
                  compact ? styles.previewTitleCompact : null,
                ]}
              >
                Mission. Product. Trust.
              </Text>
            </View>
          </View>
          <View style={styles.previewStack}>
            <MetricFreePill>Qualitative product story</MetricFreePill>
            <MetricFreePill>No projections or offering terms</MetricFreePill>
            <MetricFreePill>Republic materials separate</MetricFreePill>
          </View>
          <View
            style={[
              styles.previewDates,
              compact ? styles.previewDatesCompact : null,
            ]}
          >
            <View style={styles.previewDateItem}>
              <Text style={styles.previewDateLabel}>Published</Text>
              <Text style={styles.previewDateValue}>
                {investorPagePublishedLabel.replace('Published ', '')}
              </Text>
            </View>
            <View
              style={[
                styles.previewDateDivider,
                compact ? styles.previewDateDividerCompact : null,
              ]}
            />
            <View style={styles.previewDateItem}>
              <Text style={styles.previewDateLabel}>Updated</Text>
              <Text style={styles.previewDateValue}>
                {investorPageUpdatedLabel.replace('Updated ', '')}
              </Text>
            </View>
          </View>
          <View
            style={styles.previewFooter}
            testID="investor-pdf-download-note"
          >
            <Download color={colors.primary} size={24} strokeWidth={2.6} />
            <Text style={styles.previewFooterText}>Web page plus PDF deck</Text>
          </View>
        </View>
      </View>

      <View testID="investor-pdf-section">
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>Deck overview</Text>
          <Text
            style={[
              styles.sectionTitle,
              compact ? styles.sectionTitleCompact : null,
            ]}
          >
            Ten sections built for a Republic-ready company story.
          </Text>
        </View>

        <View style={styles.sectionGrid}>
          {deckSections.map((section) => (
            <DeckSectionCard key={section.eyebrow} {...section} />
          ))}
        </View>
      </View>

      <View style={styles.marketBand} testID="investor-pdf-section">
        <View
          style={[
            styles.marketHeader,
            compact ? styles.marketHeaderCompact : null,
          ]}
        >
          <View style={styles.marketIcon}>
            <LineChart color={colors.surface} size={28} strokeWidth={2.6} />
          </View>
          <View style={styles.marketHeaderCopy}>
            <Text style={styles.eyebrow}>Market proof</Text>
            <Text
              style={[
                styles.marketTitle,
                compact ? styles.sectionTitleCompact : null,
              ]}
            >
              Social products can become very large when they earn trust.
            </Text>
            <Text style={styles.marketIntro}>
              Public social and community companies show that attention,
              identity, community, and useful discovery can support large
              audiences and meaningful revenue.
            </Text>
          </View>
        </View>
        <View style={styles.marketStatGrid}>
          {marketStats.map((stat) => (
            <MarketStatCard key={stat.company} {...stat} />
          ))}
        </View>
        <Text style={styles.marketDisclaimer}>
          These public-company examples are market context only and are not
          projections, forecasts, or expected Frienzzi outcomes.
        </Text>
      </View>

      <View style={styles.whyNowBand} testID="investor-pdf-section">
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>Why now</Text>
          <Text
            style={[
              styles.sectionTitle,
              compact ? styles.sectionTitleCompact : null,
            ]}
          >
            The opportunity is not just another feed. It is a better trust
            model.
          </Text>
          <Text style={styles.sectionIntro}>
            Frienzzi is being built for people who still want social connection,
            but want more control, clearer expectations, and communities that
            feel worth inviting real friends into.
          </Text>
        </View>
        <View style={styles.whyNowGrid}>
          {whyNowPoints.map((point) => (
            <View key={point} style={styles.whyNowPoint}>
              <CheckCircle2
                color={colors.success}
                size={21}
                strokeWidth={2.6}
              />
              <Text style={styles.whyNowText}>{point}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.differentiatorBand} testID="investor-pdf-section">
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>What makes Frienzzi different</Text>
          <Text
            style={[
              styles.sectionTitle,
              compact ? styles.sectionTitleCompact : null,
            ]}
          >
            Public, friends, communities, and transparency in one place.
          </Text>
        </View>
        <View style={styles.differentiatorGrid}>
          {differentiators.map((differentiator) => (
            <DifferentiatorCard
              key={differentiator.label}
              {...differentiator}
            />
          ))}
        </View>
      </View>

      <View style={styles.productTour} testID="investor-pdf-section">
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>Product look</Text>
          <Text
            style={[
              styles.sectionTitle,
              compact ? styles.sectionTitleCompact : null,
            ]}
          >
            See the product experience Frienzzi is building.
          </Text>
          <Text style={styles.sectionIntro}>
            These demo views show the feed, conversation, Garage, and
            transparency experience without exposing private posts, names, email
            addresses, or real user data.
          </Text>
        </View>
        <View style={styles.captureGrid}>
          {captures.map((capture) => (
            <ProductCaptureCard key={capture.variant} {...capture} />
          ))}
        </View>
      </View>

      <View style={styles.momentumBand} testID="investor-pdf-section">
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>Use of momentum</Text>
          <Text
            style={[
              styles.sectionTitle,
              compact ? styles.sectionTitleCompact : null,
            ]}
          >
            Keep building the product and the community before broader scale.
          </Text>
          <Text style={styles.sectionIntro}>
            Until Republic materials are official, Frienzzi is describing
            momentum, not use of funds. The focus stays on what the next phase
            should make stronger.
          </Text>
        </View>
        <View style={styles.momentumGrid}>
          {momentumPoints.map((point) => (
            <View key={point} style={styles.momentumPoint}>
              <Sparkles color={colors.primary} size={20} strokeWidth={2.6} />
              <Text style={styles.momentumText}>{point}</Text>
            </View>
          ))}
        </View>
      </View>

      <View
        style={[styles.founderBand, compact ? styles.founderBandCompact : null]}
        testID="investor-pdf-section"
      >
        <View
          style={[styles.founderCopy, compact ? styles.panelCompact : null]}
        >
          <Text style={styles.eyebrow}>Founder note</Text>
          <Text
            style={[
              styles.founderTitle,
              compact ? styles.sectionTitleCompact : null,
            ]}
          >
            A note from Jason Girdner, President of Frienzzi.
          </Text>
          <Text style={styles.founderBody}>
            Frienzzi is personal to me because I believe people still want a
            social network that feels useful, respectful, and understandable.
            The goal is not to copy the loudest parts of the internet. The goal
            is to build a place where real friends and real communities can
            share without losing trust in the platform.
          </Text>
          <Text style={styles.founderClosing}>
            Frienzzi is not trying to be the loudest social network. It is
            trying to be the one people can trust enough to invite their real
            friends into.
          </Text>
        </View>
        <View
          style={[styles.founderList, compact ? styles.panelCompact : null]}
        >
          {founderPoints.map((point) => (
            <View key={point} style={styles.founderPoint}>
              <CheckCircle2
                color={colors.success}
                size={22}
                strokeWidth={2.6}
              />
              <Text style={styles.founderPointText}>{point}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.faqBand} testID="investor-pdf-section">
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>Investor FAQ</Text>
          <Text
            style={[
              styles.sectionTitle,
              compact ? styles.sectionTitleCompact : null,
            ]}
          >
            Short answers before Republic materials are live.
          </Text>
        </View>
        <View style={styles.faqGrid}>
          {faqs.map((faq) => (
            <FaqItem key={faq.question} {...faq} />
          ))}
        </View>
      </View>

      <View style={styles.republicBand} testID="investor-pdf-section">
        <View
          style={[
            styles.republicHeader,
            compact ? styles.republicHeaderCompact : null,
          ]}
        >
          <View style={styles.republicIcon}>
            <UserCheck color={colors.surface} size={28} strokeWidth={2.6} />
          </View>
          <View style={styles.republicHeaderCopy}>
            <Text style={styles.eyebrow}>About Republic</Text>
            <Text
              style={[
                styles.sectionTitle,
                compact ? styles.sectionTitleCompact : null,
              ]}
            >
              Republic would be the official place to review any future
              campaign.
            </Text>
            <Text style={styles.sectionIntro}>
              This page explains why Frienzzi is interesting. Republic would
              handle the official campaign materials, disclosures, eligibility,
              and investment workflow if Frienzzi opens a campaign.
            </Text>
            <Pressable
              accessibilityRole="link"
              onPress={() =>
                openSource('https://republic.com/learn/issuers/how-it-works')
              }
              style={({ pressed }) => [
                styles.republicSourceLink,
                pressed ? styles.marketSourceLinkPressed : null,
              ]}
            >
              <Text style={styles.republicSourceText}>
                Read the Republic issuer process
              </Text>
              <ExternalLink
                color={colors.secondary}
                size={15}
                strokeWidth={2.6}
              />
            </Pressable>
          </View>
        </View>
        <View style={styles.republicExplainerGrid}>
          {republicExplainers.map((item) => (
            <RepublicExplainerCard key={item.title} {...item} />
          ))}
        </View>
      </View>

      <View style={styles.complianceBand} testID="investor-pdf-card">
        <Text style={styles.eyebrow}>Republic readiness</Text>
        <Text style={styles.complianceTitle}>
          Company context now. Offering details later.
        </Text>
        <Text style={styles.complianceBody}>
          If Frienzzi launches a Republic campaign, the actual offering
          materials, disclosures, terms, and investor communications will live
          through the appropriate Republic and regulatory process. This page
          stays focused on product, mission, and public company context.
        </Text>
      </View>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    minWidth: 230,
  },
  actionFull: {
    width: '100%',
  },
  brandRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  brandText: {
    color: colors.secondary,
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 0,
    textTransform: 'lowercase',
  },
  captureCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 260,
    overflow: 'hidden',
  },
  captureCardBody: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  captureCardTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 25,
  },
  captureCopy: {
    gap: spacing.sm,
    padding: spacing.lg,
  },
  captureDot: {
    backgroundColor: colors.primary,
    borderRadius: 7,
    height: 14,
    width: 14,
  },
  captureGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  captureHeaderRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  captureScreen: {
    backgroundColor: '#fbf8ff',
    borderBottomColor: colors.border,
    borderBottomWidth: 1,
    minHeight: 292,
    padding: spacing.lg,
  },
  captureSort: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
  },
  captureTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  captureTopBar: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  captureTopText: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  commentAvatar: {
    backgroundColor: colors.primary,
    borderRadius: 15,
    height: 30,
    width: 30,
  },
  commentAvatarAlt: {
    backgroundColor: colors.accent,
  },
  commentBubble: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    gap: 2,
    padding: spacing.md,
  },
  commentName: {
    color: colors.text,
    fontSize: 13,
    fontWeight: '900',
  },
  commentRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  commentText: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  composerPreview: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
    padding: spacing.sm,
  },
  composerSend: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  composerSendText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
  },
  composerText: {
    color: colors.mutedText,
    flex: 1,
    fontSize: 13,
  },
  complianceBand: {
    backgroundColor: colors.accentSoft,
    borderColor: '#cae8ff',
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    marginTop: spacing.xxl,
    padding: spacing.xl,
  },
  complianceBody: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 24,
    maxWidth: 840,
  },
  complianceTitle: {
    color: colors.text,
    fontSize: 26,
    fontWeight: '900',
    lineHeight: 31,
  },
  communityFundingBody: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 21,
  },
  communityFundingCallout: {
    alignItems: 'flex-start',
    backgroundColor: colors.surface,
    borderColor: '#f5d7e8',
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    maxWidth: 680,
    padding: spacing.lg,
  },
  communityFundingCopy: {
    flex: 1,
    gap: spacing.xs,
  },
  communityFundingEyebrow: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  communityFundingIcon: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  communityFundingTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
    lineHeight: 24,
  },
  demoActionRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  demoActionText: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  demoAvatar: {
    backgroundColor: colors.secondary,
    borderRadius: 18,
    height: 36,
    width: 36,
  },
  demoBody: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 20,
    marginTop: spacing.md,
  },
  demoIdentityRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  demoMedia: {
    alignItems: 'center',
    backgroundColor: colors.warmAccent,
    borderRadius: 8,
    height: 92,
    justifyContent: 'center',
    marginTop: spacing.md,
  },
  demoMeta: {
    color: colors.mutedText,
    fontSize: 12,
  },
  demoName: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  demoPost: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    padding: spacing.lg,
  },
  disclaimer: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 20,
    maxWidth: 620,
  },
  disclaimerCompact: {
    marginBottom: spacing.sm,
  },
  differentiatorBand: {
    marginTop: spacing.xxl,
  },
  differentiatorBody: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 23,
  },
  differentiatorCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 260,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  differentiatorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  differentiatorLabel: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  differentiatorTitle: {
    color: colors.text,
    fontSize: 21,
    fontWeight: '900',
    lineHeight: 26,
  },
  eyebrow: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  founderBand: {
    alignItems: 'stretch',
    backgroundColor: colors.surfaceTint,
    borderColor: '#f5d7e8',
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.xl,
    marginTop: spacing.xxl,
    padding: spacing.xl,
  },
  founderBandCompact: {
    flexDirection: 'column',
  },
  founderBody: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 25,
  },
  founderClosing: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
    lineHeight: 25,
  },
  founderCopy: {
    flex: 1.3,
    gap: spacing.md,
  },
  founderList: {
    flex: 1,
    gap: spacing.md,
    justifyContent: 'center',
  },
  founderPoint: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  founderPointText: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    lineHeight: 22,
  },
  founderTitle: {
    color: colors.text,
    fontSize: 30,
    fontWeight: '900',
    lineHeight: 36,
  },
  garageHero: {
    alignItems: 'center',
    backgroundColor: colors.secondary,
    borderRadius: 8,
    height: 128,
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  garageMeta: {
    color: colors.mutedText,
    fontSize: 14,
  },
  garageStat: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    padding: spacing.md,
  },
  garageStatLabel: {
    color: colors.mutedText,
    fontSize: 12,
    marginTop: 2,
  },
  garageStatValue: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  garageStats: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  garageTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  faqAnswer: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 23,
  },
  faqBand: {
    marginTop: spacing.xxl,
  },
  faqGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  faqItem: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 260,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  faqQuestion: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
    lineHeight: 23,
  },
  hero: {
    alignItems: 'stretch',
    flexDirection: 'row',
    gap: spacing.xl,
    marginBottom: spacing.xxl,
  },
  heroActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  heroActionsCompact: {
    flexDirection: 'column',
  },
  heroCompact: {
    flexDirection: 'column',
    paddingTop: spacing.xxl + spacing.xl,
  },
  heroCopy: {
    flex: 1.35,
    gap: spacing.lg,
    justifyContent: 'center',
  },
  heroPreview: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    gap: spacing.xl,
    padding: spacing.xl,
  },
  heroPreviewCompact: {
    gap: spacing.lg,
    padding: spacing.lg,
  },
  heroSubtitle: {
    color: colors.mutedText,
    fontSize: 20,
    lineHeight: 30,
    maxWidth: 680,
  },
  heroTitle: {
    color: colors.text,
    fontSize: 54,
    fontWeight: '900',
    lineHeight: 60,
    maxWidth: 760,
  },
  heroTitleCompact: {
    fontSize: 36,
    lineHeight: 42,
  },
  panelCompact: {
    width: '100%',
  },
  marketBand: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xl,
    marginTop: spacing.xxl,
    padding: spacing.xl,
  },
  marketCompany: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  marketContext: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 21,
  },
  marketDisclaimer: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 20,
  },
  marketHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.lg,
  },
  marketHeaderCompact: {
    flexDirection: 'column',
  },
  marketHeaderCopy: {
    flex: 1,
    gap: spacing.md,
  },
  marketIcon: {
    alignItems: 'center',
    backgroundColor: colors.secondary,
    borderRadius: 8,
    height: 56,
    justifyContent: 'center',
    width: 56,
  },
  marketIntro: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 24,
    maxWidth: 860,
  },
  marketMetric: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 21,
  },
  marketSourceLink: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: 'auto',
    paddingVertical: spacing.xs,
  },
  marketSourceLinkPressed: {
    opacity: 0.7,
  },
  marketSourceText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
  },
  marketStatCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: '#f5d7e8',
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 230,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  marketStatGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  marketTitle: {
    color: colors.text,
    fontSize: 32,
    fontWeight: '900',
    lineHeight: 38,
    maxWidth: 860,
  },
  marketValue: {
    color: colors.primary,
    fontSize: 38,
    fontWeight: '900',
    lineHeight: 42,
  },
  momentumBand: {
    backgroundColor: colors.accentSoft,
    borderColor: '#cae8ff',
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xl,
    marginTop: spacing.xxl,
    padding: spacing.xl,
  },
  momentumGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  momentumPoint: {
    alignItems: 'flex-start',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 250,
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
  },
  momentumText: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 22,
  },
  pill: {
    backgroundColor: colors.surfaceTint,
    borderColor: '#f5d7e8',
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  pillText: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '800',
  },
  previewEyebrow: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '800',
  },
  previewDateDivider: {
    backgroundColor: colors.border,
    height: '100%',
    minHeight: 42,
    width: 1,
  },
  previewDateDividerCompact: {
    height: 1,
    minHeight: 1,
    width: '100%',
  },
  previewDateItem: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 120,
  },
  previewDateLabel: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  previewDates: {
    alignItems: 'stretch',
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
  },
  previewDatesCompact: {
    flexDirection: 'column',
  },
  previewDateValue: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '900',
    lineHeight: 21,
  },
  previewFooter: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
  },
  previewFooterText: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '800',
  },
  previewStack: {
    gap: spacing.md,
  },
  previewTitle: {
    color: colors.surface,
    flexShrink: 1,
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 27,
  },
  previewTitleCompact: {
    fontSize: 20,
    lineHeight: 24,
  },
  previewTitleWrap: {
    flex: 1,
    minWidth: 0,
  },
  previewTop: {
    alignItems: 'center',
    backgroundColor: colors.secondary,
    borderRadius: 8,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.lg,
  },
  previewTopCompact: {
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  productTour: {
    marginTop: spacing.xxl,
  },
  republicBand: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xl,
    marginTop: spacing.xxl,
    padding: spacing.xl,
  },
  republicExplainerBody: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 23,
  },
  republicExplainerCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: '#f5d7e8',
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 240,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  republicExplainerGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  republicExplainerTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
    lineHeight: 23,
  },
  republicHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.lg,
  },
  republicHeaderCompact: {
    flexDirection: 'column',
  },
  republicHeaderCopy: {
    flex: 1,
    gap: spacing.md,
  },
  republicIcon: {
    alignItems: 'center',
    backgroundColor: colors.secondary,
    borderRadius: 8,
    height: 56,
    justifyContent: 'center',
    width: 56,
  },
  republicSourceLink: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
  },
  republicSourceText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
  },
  sectionCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 250,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  sectionCardBody: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 23,
  },
  sectionCardTitle: {
    color: colors.text,
    fontSize: 21,
    fontWeight: '900',
    lineHeight: 26,
  },
  sectionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  sectionHeader: {
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  sectionIcon: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    height: 46,
    justifyContent: 'center',
    width: 46,
  },
  sectionIntro: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 24,
    maxWidth: 820,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 34,
    fontWeight: '900',
    lineHeight: 40,
    maxWidth: 860,
  },
  sectionTitleCompact: {
    fontSize: 28,
    lineHeight: 34,
  },
  whyNowBand: {
    backgroundColor: colors.surfaceTint,
    borderColor: '#f5d7e8',
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xl,
    marginTop: spacing.xxl,
    padding: spacing.xl,
  },
  whyNowGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  whyNowPoint: {
    alignItems: 'flex-start',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexBasis: 250,
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
  },
  whyNowText: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 22,
  },
  trustHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  trustMeta: {
    color: colors.mutedText,
    fontSize: 12,
  },
  trustRow: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    padding: spacing.md,
  },
  trustText: {
    color: colors.text,
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
  },
  trustTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
});
